import { useState } from 'react'
import type { DeckCreate } from '../types'
import { useLocale } from '../i18n/context'
import { useKeyboardInset } from '../hooks/useKeyboardInset'
import { useSwipeNavBlock } from '../hooks/useSwipeNavBlock'
import { useAuthStore, selectUser } from '../stores/authStore'
import LanguageSelector from './shared/LanguageSelector'

interface Props {
  onSave: (body: DeckCreate) => void
  onClose: () => void
  isSaving?: boolean
}

// Short creation scenario (redesign): name + language only, then
// straight into the new (empty) deck. Description, public visibility, and
// daily-queue membership all already have a home in deck settings/menu
// (DeckDetailPage's overflow menu + LearningToggleSwitch) and default
// server-side (description=None, is_public=False, is_active_for_learning=
// True) — so this form simply omits them rather than duplicating controls.
export default function DeckCreateModal({ onSave, onClose, isSaving }: Props) {
  const { tr } = useLocale()
  const dc = tr.deckCreate

  const [title, setTitle] = useState('')
  // A deck always has a language: it starts as the learner's target language
  // (Fill writes Definitions and Examples in it) and there is no unset option.
  // The server applies the same default for a create that sends none.
  const user = useAuthStore(selectUser)
  const [language, setLanguage] = useState(user?.target_language || 'en')

  // Anchored to the bottom of the screen on mobile (see the media query
  // below) — translating the panel up by the keyboard-covered delta keeps
  // the Create button flush above the keyboard even on WebViews that don't
  // resize the layout viewport for the IME (same fix as the deck-feed
  // composer; see useKeyboardInset.ts).
  const keyboardInset = useKeyboardInset()
  // ADR-106 P3: mounted only while open, so it blocks for its whole lifetime.
  useSwipeNavBlock(true, 'deck-create-modal')

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!title.trim()) return
    const body: DeckCreate = {
      title: title.trim(),
      language,
    }
    onSave(body)
  }

  const canSave = title.trim() && !isSaving

  return (
    <div
      className="deck-create-overlay"
      style={{
        position: 'fixed', inset: 0, zIndex: 'var(--z-modal-backdrop)' as never,
        background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <style>{`
        @media (max-width: 767px) {
          .deck-create-overlay { align-items: flex-end !important; padding: 0 !important; }
          .deck-create-panel { width: 100% !important; max-width: none !important; border-radius: 20px 20px 0 0 !important; }
        }
      `}</style>
      <div
        className="deck-create-panel"
        style={{
          background: 'var(--bg-surface)', borderRadius: 'var(--radius-md)',
          boxShadow: 'var(--shadow-float)', width: '100%', maxWidth: 420,
          transform: keyboardInset > 0 ? `translateY(-${keyboardInset}px)` : undefined,
          transition: 'transform 0.12s ease-out',
        }}
      >
        {/* Header */}
        <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' }}>{dc.title}</h2>
          <button
            onClick={onClose}
            aria-label={dc.closeAria}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: 22, padding: 4, lineHeight: 1 }}
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 16, paddingBottom: 'max(24px, env(safe-area-inset-bottom, 0px))' }}>
          {/* Name */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <label style={labelStyle}>{dc.nameLabel} <span style={{ color: 'var(--danger)' }}>{dc.required}</span></label>
            <input
              autoFocus
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={dc.namePlaceholder}
              style={inputStyle}
              onFocus={(e) => (e.currentTarget.style.borderColor = 'var(--accent)')}
              onBlur={(e) => (e.currentTarget.style.borderColor = 'var(--border)')}
            />
          </div>

          {/* Language */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <label style={labelStyle}>{dc.languageLabel}</label>
            <LanguageSelector
              value={language}
              onChange={setLanguage}
            />
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '10px 20px', background: 'var(--bg-secondary)', color: 'var(--text-primary)',
                border: '1px solid var(--border)', borderRadius: 'var(--radius-pill)', fontSize: 14,
                fontWeight: 500, cursor: 'pointer',
              }}
            >
              {dc.cancelBtn}
            </button>
            <button
              type="submit"
              disabled={!canSave}
              style={{
                padding: '10px 24px', background: 'var(--accent-fill)', color: 'var(--text-on-accent)',
                border: 'none', borderRadius: 'var(--radius-pill)', fontSize: 14,
                fontWeight: 600, cursor: canSave ? 'pointer' : 'not-allowed', opacity: canSave ? 1 : 0.5,
              }}
            >
              {isSaving ? dc.creating : dc.createBtn}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

const labelStyle: React.CSSProperties = {
  fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)',
  textTransform: 'uppercase', letterSpacing: '0.05em',
}

const inputStyle: React.CSSProperties = {
  padding: '10px 14px', fontSize: 14,
  background: 'var(--bg-secondary)', border: '1px solid var(--border)',
  borderRadius: 'var(--radius-sm)', color: 'var(--text-primary)', outline: 'none',
  fontFamily: 'var(--font-ui)', width: '100%', boxSizing: 'border-box',
}
